import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card } from '../components/SubscriptionAppCard';
import { Badge } from '../components/SubscriptionAppBadge';
import { Button } from '../components/SubscriptionAppButton';
import { StatCard, TrendIndicator } from '../components/StatCard';
import { DateRangePicker } from '../components/DateRangePicker';
import { SearchBar } from '../components/SearchBar';
import { TableFilter } from '../components/TableFilter';
import type { Filter } from '../components/TableFilter';
import { NavBar } from '../components/NavBar';
import { DropdownMenu } from '../components/SubscriptionAppDropdownMenu';
import { Package, Download, Plus, X, MoreVertical, Printer, Search, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { ExportButton } from '../components/ExportButton';
// Mock data for orders
const mockOrders = [{
  id: '1042',
  customerName: 'Sarah Johnson',
  customerEmail: 'sarah.j@example.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23144',
  date: '2025-12-14T10:30:00',
  items: 6,
  total: 126.5,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1041',
  customerName: 'James Anderson',
  customerEmail: 'james.a@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23138',
  date: '2025-12-14T09:15:00',
  items: 6,
  total: 315.0,
  paymentStatus: 'In Retry',
  fulfillmentStatus: 'Unfulfilled',
  retryCount: 2,
  maxRetries: 4
}, {
  id: '1040',
  customerName: 'Michael Chen',
  customerEmail: 'demov18@winery.com',
  type: 'One-time',
  date: '2025-12-13T16:45:00',
  items: 3,
  total: 189.5,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1039',
  customerName: 'Emma Williams',
  customerEmail: 'demov211@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23139',
  date: '2025-12-13T14:20:00',
  items: 6,
  total: 245.0,
  paymentStatus: 'Failed',
  fulfillmentStatus: 'Unfulfilled'
}, {
  id: '1038',
  customerName: 'Ethan White',
  customerEmail: 'ethan.w@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23130',
  date: '2025-12-12T11:30:00',
  items: 12,
  total: 298.75,
  paymentStatus: 'In Retry',
  fulfillmentStatus: 'Unfulfilled',
  retryCount: 3,
  maxRetries: 4
}, {
  id: '1037',
  customerName: 'Olivia Martinez',
  customerEmail: 'olivia.m@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23137',
  date: '2025-12-12T10:00:00',
  items: 12,
  total: 98.75,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1036',
  customerName: 'William Taylor',
  customerEmail: 'w.taylor@winery.com',
  type: 'One-time',
  date: '2025-12-11T15:30:00',
  items: 12,
  total: 275.5,
  paymentStatus: 'Failed',
  fulfillmentStatus: 'Unfulfilled'
}, {
  id: '1035',
  customerName: 'Sophia Brown',
  customerEmail: 'sophia.b@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23135',
  date: '2025-12-11T09:45:00',
  items: 3,
  total: 142.0,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}, {
  id: '1034',
  customerName: 'Victoria Moore',
  customerEmail: 'victoria.m@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23121',
  date: '2025-12-10T13:20:00',
  items: 6,
  total: 195.0,
  paymentStatus: 'In Retry',
  fulfillmentStatus: 'Unfulfilled',
  retryCount: 1,
  maxRetries: 4
}, {
  id: '1033',
  customerName: 'Daniel Scott',
  customerEmail: 'daniel.s@winery.com',
  type: 'Subscription',
  subscriptionId: 'SUB-23120',
  date: '2025-12-10T11:00:00',
  items: 12,
  total: 285.5,
  paymentStatus: 'Paid',
  fulfillmentStatus: 'Fulfilled'
}];
export function OrdersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // State
  const [dateRange, setDateRange] = useState('Last 30 days');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage_, setCurrentPage_] = useState(1);
  const pageSize = 10;
  // Stats date range state
  const [statsTimeRange, setStatsTimeRange] = useState('Last 30 Days');
  const statsTimeOptions = ['Last 7 Days', 'Last 30 Days', 'Last 60 Days', 'Last 90 Days'] as const;
  // Filter state
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({
    status: [],
    orderType: [],
    paymentStatus: [],
    fulfillmentStatus: [],
    customer: [],
    orderId: [],
    date: [],
    items: [],
    total: []
  });
  const [statusFilter, setStatusFilter] = useState('All');
  const [orderTypeFilter, setOrderTypeFilter] = useState('All');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('All');
  const [addedFilters, setAddedFilters] = useState<Set<string>>(new Set());
  const [openFilterDropdowns, setOpenFilterDropdowns] = useState<Set<string>>(new Set());
  const [showAddFilterDropdown, setShowAddFilterDropdown] = useState(false);
  // Custom date ranges state
  const [customDateRanges, setCustomDateRanges] = useState<Record<string, {
    startDate: string;
    endDate: string;
  }>>({});
  // State for managing which order's dropdown is open
  const [openOrderDropdown, setOpenOrderDropdown] = useState<string | null>(null);
  // Sort state
  const [sortConfig, setSortConfig] = useState<{
    key: string;
    direction: 'asc' | 'desc';
  } | null>({
    key: 'date',
    direction: 'desc'
  });
  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };
  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };
  // Calculate stats
  const stats = useMemo(() => {
    const now = new Date();
    const getDaysForRange = (range: string) => {
      switch (range) {
        case 'Last 7 Days':
          return 7;
        case 'Last 30 Days':
          return 30;
        case 'Last 60 Days':
          return 60;
        case 'Last 90 Days':
          return 90;
        default:
          return 30;
      }
    };
    const days = getDaysForRange(statsTimeRange);
    const filteredOrders = mockOrders.filter(order => {
      const orderDate = new Date(order.date);
      const daysDiff = Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff >= 0 && daysDiff <= days;
    });
    const totalOrders = filteredOrders.length;
    const totalRevenue = filteredOrders.reduce((sum, order) => sum + order.total, 0);
    const paymentFailed = filteredOrders.filter(o => o.paymentStatus === 'Failed').length;
    const pendingFulfillment = filteredOrders.filter(o => o.fulfillmentStatus === 'Unfulfilled').length;
    return {
      totalOrders,
      totalRevenue,
      paymentFailed,
      pendingFulfillment
    };
  }, [statsTimeRange]);
  // Available filters
  const availableFilters: Filter[] = [{
    id: 'status',
    label: 'Status',
    options: [{
      id: '1',
      label: 'Pending',
      value: 'pending'
    }, {
      id: '2',
      label: 'Processing',
      value: 'processing'
    }, {
      id: '3',
      label: 'Completed',
      value: 'completed'
    }, {
      id: '4',
      label: 'Failed',
      value: 'failed'
    }, {
      id: '5',
      label: 'Cancelled',
      value: 'cancelled'
    }, {
      id: '6',
      label: 'Refunded',
      value: 'refunded'
    }],
    selectedValues: filterValues.status || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('status')
  }, {
    id: 'orderId',
    label: 'Order #',
    options: [],
    selectedValue: filterValues.orderId?.[0] || '',
    type: 'search',
    isOpen: openFilterDropdowns.has('orderId')
  }, {
    id: 'customer',
    label: 'Customer',
    options: [{
      id: '1',
      label: 'A-F',
      value: 'a-f'
    }, {
      id: '2',
      label: 'G-M',
      value: 'g-m'
    }, {
      id: '3',
      label: 'N-S',
      value: 'n-s'
    }, {
      id: '4',
      label: 'T-Z',
      value: 't-z'
    }],
    selectedValues: filterValues.customer || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('customer')
  }, {
    id: 'orderType',
    label: 'Type',
    options: [{
      id: '1',
      label: 'Subscription',
      value: 'subscription'
    }, {
      id: '2',
      label: 'One-time',
      value: 'one-time'
    }],
    selectedValues: filterValues.orderType || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('orderType')
  }, {
    id: 'date',
    label: 'Date',
    options: [{
      id: '1',
      label: 'Today',
      value: 'today'
    }, {
      id: '2',
      label: 'Last 7 days',
      value: 'last-7-days'
    }, {
      id: '3',
      label: 'Last 30 days',
      value: 'last-30-days'
    }, {
      id: '4',
      label: 'Last 3 months',
      value: 'last-3-months'
    }, {
      id: '5',
      label: 'This month',
      value: 'this-month'
    }, {
      id: '6',
      label: 'Last month',
      value: 'last-month'
    }, {
      id: '7',
      label: 'Custom',
      value: 'custom'
    }],
    selectedValues: filterValues.date || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('date'),
    customDateRange: customDateRanges['date']
  }, {
    id: 'items',
    label: 'Items',
    options: [{
      id: '1',
      label: '1-3 items',
      value: '1-3'
    }, {
      id: '2',
      label: '4-6 items',
      value: '4-6'
    }, {
      id: '3',
      label: '7-12 items',
      value: '7-12'
    }, {
      id: '4',
      label: '12+ items',
      value: '12-plus'
    }],
    selectedValues: filterValues.items || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('items')
  }, {
    id: 'total',
    label: 'Total',
    options: [{
      id: '1',
      label: 'Under $100',
      value: 'under-100'
    }, {
      id: '2',
      label: '$100 - $200',
      value: '100-200'
    }, {
      id: '3',
      label: '$200 - $300',
      value: '200-300'
    }, {
      id: '4',
      label: 'Over $300',
      value: 'over-300'
    }],
    selectedValues: filterValues.total || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('total')
  }, {
    id: 'paymentStatus',
    label: 'Payment',
    options: [{
      id: '1',
      label: 'Paid',
      value: 'paid'
    }, {
      id: '2',
      label: 'In Retry',
      value: 'in-retry'
    }, {
      id: '3',
      label: 'Failed',
      value: 'failed'
    }, {
      id: '4',
      label: 'Refunded',
      value: 'refunded'
    }, {
      id: '5',
      label: 'Pending',
      value: 'pending'
    }],
    selectedValues: filterValues.paymentStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('paymentStatus')
  }, {
    id: 'fulfillmentStatus',
    label: 'Fulfillment',
    options: [{
      id: '1',
      label: 'Fulfilled',
      value: 'fulfilled'
    }, {
      id: '2',
      label: 'Unfulfilled',
      value: 'unfulfilled'
    }, {
      id: '3',
      label: 'Partially Fulfilled',
      value: 'partially'
    }],
    selectedValues: filterValues.fulfillmentStatus || [],
    multiSelect: true,
    isOpen: openFilterDropdowns.has('fulfillmentStatus')
  }];
  // Filter and sort orders
  const filteredOrders = useMemo(() => {
    let filtered = mockOrders.filter(order => {
      if (filterValues.status && filterValues.status.length > 0) {
        const statusMatch = filterValues.status.some(value => {
          if (value === 'pending') return order.fulfillmentStatus === 'Unfulfilled';
          if (value === 'processing') return order.fulfillmentStatus === 'Partially';
          if (value === 'completed') return order.fulfillmentStatus === 'Fulfilled';
          if (value === 'failed') return order.paymentStatus === 'Failed';
          return false;
        });
        if (!statusMatch) return false;
      }
      if (filterValues.orderId && filterValues.orderId.length > 0 && filterValues.orderId[0].trim()) {
        const searchValue = filterValues.orderId[0].toLowerCase();
        if (!order.id.toLowerCase().includes(searchValue)) return false;
      }
      if (filterValues.customer && filterValues.customer.length > 0) {
        const firstLetter = order.customerName.charAt(0).toLowerCase();
        const customerMatch = filterValues.customer.some(value => {
          if (value === 'a-f') return firstLetter >= 'a' && firstLetter <= 'f';
          if (value === 'g-m') return firstLetter >= 'g' && firstLetter <= 'm';
          if (value === 'n-s') return firstLetter >= 'n' && firstLetter <= 's';
          if (value === 't-z') return firstLetter >= 't' && firstLetter <= 'z';
          return false;
        });
        if (!customerMatch) return false;
      }
      if (filterValues.orderType && filterValues.orderType.length > 0) {
        const typeMatch = filterValues.orderType.some(value => {
          if (value === 'subscription') return order.type === 'Subscription';
          if (value === 'one-time') return order.type === 'One-time';
          return false;
        });
        if (!typeMatch) return false;
      }
      if (filterValues.date && filterValues.date.length > 0) {
        const orderDate = new Date(order.date);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - orderDate.getTime()) / (1000 * 60 * 60 * 24));
        const dateMatch = filterValues.date.some(value => {
          if (value === 'today') return daysDiff === 0;
          if (value === 'last-7-days') return daysDiff >= 0 && daysDiff <= 7;
          if (value === 'last-30-days') return daysDiff >= 0 && daysDiff <= 30;
          if (value === 'last-3-months') return daysDiff >= 0 && daysDiff <= 90;
          if (value === 'this-month') {
            return orderDate.getMonth() === now.getMonth() && orderDate.getFullYear() === now.getFullYear();
          }
          if (value === 'last-month') {
            const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
            return orderDate.getMonth() === lastMonth.getMonth() && orderDate.getFullYear() === lastMonth.getFullYear();
          }
          if (value === 'custom') {
            const dateRange = customDateRanges['date'];
            if (dateRange && dateRange.startDate && dateRange.endDate) {
              const start = new Date(dateRange.startDate);
              const end = new Date(dateRange.endDate);
              end.setHours(23, 59, 59, 999);
              return orderDate >= start && orderDate <= end;
            }
            return true;
          }
          return false;
        });
        if (!dateMatch) return false;
      }
      if (filterValues.items && filterValues.items.length > 0) {
        const itemsMatch = filterValues.items.some(value => {
          if (value === '1-3') return order.items >= 1 && order.items <= 3;
          if (value === '4-6') return order.items >= 4 && order.items <= 6;
          if (value === '7-12') return order.items >= 7 && order.items <= 12;
          if (value === '12-plus') return order.items > 12;
          return false;
        });
        if (!itemsMatch) return false;
      }
      if (filterValues.total && filterValues.total.length > 0) {
        const totalMatch = filterValues.total.some(value => {
          if (value === 'under-100') return order.total < 100;
          if (value === '100-200') return order.total >= 100 && order.total < 200;
          if (value === '200-300') return order.total >= 200 && order.total < 300;
          if (value === 'over-300') return order.total >= 300;
          return false;
        });
        if (!totalMatch) return false;
      }
      if (filterValues.paymentStatus && filterValues.paymentStatus.length > 0) {
        const paymentMatch = filterValues.paymentStatus.some(value => {
          if (value === 'in-retry') return order.paymentStatus === 'In Retry';
          return order.paymentStatus.toLowerCase() === value.toLowerCase();
        });
        if (!paymentMatch) return false;
      }
      if (filterValues.fulfillmentStatus && filterValues.fulfillmentStatus.length > 0) {
        const fulfillmentMatch = filterValues.fulfillmentStatus.some(value => {
          if (value === 'fulfilled') return order.fulfillmentStatus === 'Fulfilled';
          if (value === 'unfulfilled') return order.fulfillmentStatus === 'Unfulfilled';
          if (value === 'partially') return order.fulfillmentStatus === 'Partially';
          return false;
        });
        if (!fulfillmentMatch) return false;
      }
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return order.id.toLowerCase().includes(query) || order.customerName.toLowerCase().includes(query) || order.customerEmail.toLowerCase().includes(query);
      }
      return true;
    });
    if (sortConfig) {
      filtered = [...filtered].sort((a, b) => {
        let aValue: any;
        let bValue: any;
        switch (sortConfig.key) {
          case 'id':
            aValue = parseInt(a.id);
            bValue = parseInt(b.id);
            break;
          case 'customer':
            aValue = a.customerName.toLowerCase();
            bValue = b.customerName.toLowerCase();
            break;
          case 'type':
            aValue = a.type;
            bValue = b.type;
            break;
          case 'date':
            aValue = new Date(a.date).getTime();
            bValue = new Date(b.date).getTime();
            break;
          case 'items':
            aValue = a.items;
            bValue = b.items;
            break;
          case 'total':
            aValue = a.total;
            bValue = b.total;
            break;
          case 'paymentStatus':
            aValue = a.paymentStatus;
            bValue = b.paymentStatus;
            break;
          case 'fulfillmentStatus':
            aValue = a.fulfillmentStatus;
            bValue = b.fulfillmentStatus;
            break;
          default:
            return 0;
        }
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
        }
        if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [filterValues, searchQuery, customDateRanges, sortConfig]);
  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / pageSize);
  const paginatedOrders = filteredOrders.slice((currentPage_ - 1) * pageSize, currentPage_ * pageSize);
  const hasActiveFilters = addedFilters.size > 0 || searchQuery.trim() !== '';
  const clearAllFilters = () => {
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [],
      items: [],
      total: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  const getPaymentStatusVariant = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    if (status === 'Paid') return 'success';
    if (status === 'In Retry') return 'warning';
    if (status === 'Failed') return 'error';
    if (status === 'Pending') return 'default';
    if (status === 'Refunded') return 'default';
    return 'default';
  };
  const getFulfillmentStatusVariant = (status: string): 'success' | 'warning' | 'info' | 'default' => {
    if (status === 'Fulfilled') return 'success';
    if (status === 'Unfulfilled') return 'warning';
    if (status === 'Partially') return 'info';
    return 'default';
  };
  const handleToggleAddFilterDropdown = () => {
    setShowAddFilterDropdown(!showAddFilterDropdown);
  };
  const handleAddFilter = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.add(filterId);
    setAddedFilters(newAddedFilters);
    switch (filterId) {
      case 'status':
        setStatusFilter('All');
        break;
      case 'orderType':
        setOrderTypeFilter('All');
        break;
      case 'paymentStatus':
        setPaymentStatusFilter('All');
        break;
    }
    setShowAddFilterDropdown(false);
  };
  const handleClearAll = () => {
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [],
      items: [],
      total: []
    });
    setAddedFilters(new Set());
    setSearchQuery('');
  };
  const handleFilterChange = (filterId: string, value: string | string[]) => {
    const newFilterValues = {
      ...filterValues,
      [filterId]: Array.isArray(value) ? value : [value]
    };
    setFilterValues(newFilterValues);
  };
  const handleFilterRemove = (filterId: string) => {
    const newAddedFilters = new Set(addedFilters);
    newAddedFilters.delete(filterId);
    setAddedFilters(newAddedFilters);
    switch (filterId) {
      case 'status':
        setStatusFilter('All');
        break;
      case 'orderType':
        setOrderTypeFilter('All');
        break;
      case 'paymentStatus':
        setPaymentStatusFilter('All');
        break;
    }
  };
  const handleToggleFilterDropdown = (filterId: string) => {
    const newOpenFilterDropdowns = new Set(openFilterDropdowns);
    newOpenFilterDropdowns.add(filterId);
    setOpenFilterDropdowns(newOpenFilterDropdowns);
  };
  const handleCustomDateChange = (filterId: string, startDate: string, endDate: string) => {
    setCustomDateRanges(prev => ({
      ...prev,
      [filterId]: {
        startDate,
        endDate
      }
    }));
  };
  const handleTotalOrdersClick = () => {
    let filterValue = 'last-30-days';
    if (statsTimeRange === 'Last 7 Days') filterValue = 'last-7-days';else if (statsTimeRange === 'Last 30 Days') filterValue = 'last-30-days';else if (statsTimeRange === 'Last 60 Days' || statsTimeRange === 'Last 90 Days') filterValue = 'last-3-months';
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [filterValue],
      items: [],
      total: []
    });
    setAddedFilters(new Set(['date']));
  };
  const handleRevenueClick = () => {
    let filterValue = 'last-30-days';
    if (statsTimeRange === 'Last 7 Days') filterValue = 'last-7-days';else if (statsTimeRange === 'Last 30 Days') filterValue = 'last-30-days';else if (statsTimeRange === 'Last 60 Days' || statsTimeRange === 'Last 90 Days') filterValue = 'last-3-months';
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: [],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [filterValue],
      items: [],
      total: []
    });
    setAddedFilters(new Set(['date']));
  };
  const handlePaymentFailedClick = () => {
    setFilterValues({
      status: [],
      orderType: [],
      paymentStatus: ['failed'],
      fulfillmentStatus: [],
      customer: [],
      orderId: [],
      date: [],
      items: [],
      total: []
    });
    setAddedFilters(new Set(['paymentStatus']));
  };
  useEffect(() => {
    if (location.state?.filterValues) {
      setFilterValues(prev => ({
        ...prev,
        ...location.state.filterValues
      }));
      const filterKeys = Object.keys(location.state.filterValues);
      if (filterKeys.length > 0) {
        setAddedFilters(new Set(filterKeys));
      }
    }
  }, [location.state]);
  const handleViewOrderInShopify = (orderId: string) => {
    console.log(`View order ${orderId} in Shopify`);
    setOpenOrderDropdown(null);
  };
  const handleSort = (columnKey: string) => {
    setSortConfig(current => {
      if (!current || current.key !== columnKey) {
        return {
          key: columnKey,
          direction: 'asc'
        };
      }
      if (current.direction === 'asc') {
        return {
          key: columnKey,
          direction: 'desc'
        };
      }
      return null;
    });
  };
  return <div className="bg-[#F1F5F9] min-h-full p-4 md:p-6" data-id="element-7911">
      {/* NavBar */}
      <div className="mb-4 md:mb-6" data-id="element-7912">
        <NavBar tabs={[]} title="Orders" data-id="element-7913" />
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-4" data-id="element-7914">
        <StatCard label="Total Orders" value={stats.totalOrders} trend={<TrendIndicator value={12} label="vs last period" direction="up" data-id="element-7916" />} onClick={handleTotalOrdersClick} data-id="element-7915" />
        <StatCard label="Revenue" value={`$${stats.totalRevenue.toLocaleString('en-US', {
        minimumFractionDigits: 2
      })}`} trend={<TrendIndicator value={8} label="vs last period" direction="up" data-id="element-7918" />} onClick={handleRevenueClick} data-id="element-7917" />
        <StatCard label="Payment Failed" value={stats.paymentFailed} secondaryValue="Requires attention" badge={stats.paymentFailed > 0 ? {
        text: 'Action Needed',
        variant: 'error'
      } : undefined} onClick={handlePaymentFailedClick} data-id="element-7919" />
        <StatCard label="Pending Fulfillment" value={stats.pendingFulfillment} secondaryValue="Ready to ship" badge={stats.pendingFulfillment > 0 ? {
        text: 'Action Needed',
        variant: 'warning'
      } : undefined} data-id="element-7920" />
      </div>

      {/* Date Range Tabs */}
      <div className="flex justify-start mb-6 md:mb-8" data-id="element-7921">
        <div className="bg-[#E2E8F0] p-1 rounded-lg inline-flex" data-id="element-7922">
          {statsTimeOptions.map(option => <button key={option} onClick={() => setStatsTimeRange(option)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-all duration-200 ${statsTimeRange === option ? 'bg-white text-[#0F172A] shadow-sm' : 'text-[#64748B] hover:text-[#475569]'}`} data-id="element-7923">
              {option}
            </button>)}
        </div>
      </div>

      <hr className="my-6 md:my-10 border-[#CBD5E1]" data-id="element-7924" />

      {/* Filter Bar */}
      <div className="bg-white border border-[#E2E8F0] rounded-lg mb-4 md:mb-6 p-4 md:p-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-7925">
        <div className="flex flex-col gap-4" data-id="element-7926">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-7927">
            <div className="w-full lg:w-80 flex-shrink-0" data-id="element-7928">
              <SearchBar placeholder="Search by order #, customer name, or email" value={searchQuery} onChange={setSearchQuery} className="w-full" data-id="element-7929" />
            </div>
            <div className="flex items-center gap-3 flex-shrink-0 ml-auto" data-id="element-7930">
              <ExportButton onExport={async type => {
              console.log(`Exporting ${type}`);
            }} data-id="element-7931" />
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyButton={true} data-id="element-7932" />
            </div>
          </div>
          {addedFilters.size > 0 && <div className="flex items-center gap-3" data-id="element-7933">
              <TableFilter filters={availableFilters} onFilterChange={handleFilterChange} onFilterRemove={handleFilterRemove} onToggleDropdown={handleToggleFilterDropdown} onCustomDateChange={handleCustomDateChange} addedFilterIds={addedFilters} onAddFilter={handleAddFilter} renderOnlyChips={true} data-id="element-7934" />
              <button onClick={handleClearAll} className="text-[13px] text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors" data-id="element-7935">
                Clear all
              </button>
            </div>}
        </div>
      </div>

      {/* Orders Table */}
      {filteredOrders.length > 0 ? <>
          <div className="bg-white rounded-lg border border-[#E2E8F0] overflow-hidden mb-4 md:mb-6 shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-7936">
            <div className="overflow-x-auto" data-id="element-7937">
              <table className="w-full" data-id="element-7938">
                <thead className="bg-[#F1F5F9]" data-id="element-7939">
                  <tr data-id="element-7940">
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7941">
                      <button onClick={() => handleSort('id')} className="flex items-center gap-2 text-xs font-semibold text-[#475569] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7942">
                        Order #
                        {sortConfig?.key === 'id' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7943" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7944" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7945" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7946">
                      <button onClick={() => handleSort('customer')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7947">
                        Customer
                        {sortConfig?.key === 'customer' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7948" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7949" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7950" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7951">
                      <button onClick={() => handleSort('type')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7952">
                        Type
                        {sortConfig?.key === 'type' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7953" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7954" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7955" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7956">
                      <button onClick={() => handleSort('date')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7957">
                        Date
                        {sortConfig?.key === 'date' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7958" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7959" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7960" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7961">
                      <button onClick={() => handleSort('items')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7962">
                        Items
                        {sortConfig?.key === 'items' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7963" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7964" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7965" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7966">
                      <button onClick={() => handleSort('total')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7967">
                        Total
                        {sortConfig?.key === 'total' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7968" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7969" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7970" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7971">
                      <button onClick={() => handleSort('paymentStatus')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7972">
                        Payment
                        {sortConfig?.key === 'paymentStatus' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7973" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7974" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7975" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left" data-id="element-7976">
                      <button onClick={() => handleSort('fulfillmentStatus')} className="flex items-center gap-2 text-xs font-semibold text-[#64748B] uppercase tracking-wider hover:text-[#0EA5E9] transition-colors" data-id="element-7977">
                        Fulfillment
                        {sortConfig?.key === 'fulfillmentStatus' ? sortConfig.direction === 'asc' ? <ArrowUp className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7978" /> : <ArrowDown className="w-3.5 h-3.5 text-[#0EA5E9]" data-id="element-7979" /> : <ArrowUpDown className="w-3.5 h-3.5 text-[#CBD5E1]" data-id="element-7980" />}
                      </button>
                    </th>
                    <th className="px-3 md:px-4 py-3 text-left text-xs font-semibold text-[#64748B] uppercase tracking-wider w-12" data-id="element-7981">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E2E8F0]" data-id="element-7982">
                  {paginatedOrders.map(order => <tr key={order.id} className={`hover:bg-[#F8FAFC] transition-colors duration-200 ease-in-out ${order.paymentStatus === 'Failed' ? 'border-l-4 border-l-[#EF4444]' : ''}`} data-id="element-7983">
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-7984">
                        <button className="font-mono text-sm text-[#0EA5E9] hover:text-[#0284C7] font-medium transition-colors duration-200 ease-in-out" data-id="element-7985">
                          #{order.id}
                        </button>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-7986">
                        <div data-id="element-7987">
                          <div className="text-sm font-medium text-[#0F172A]" data-id="element-7988">
                            {order.customerName}
                          </div>
                          <div className="text-[13px] text-[#94A3B8] mt-0.5" data-id="element-7989">
                            {order.customerEmail}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-7990">
                        <Badge variant={order.type === 'Subscription' ? 'info' : 'default'} data-id="element-7991">
                          {order.type}
                        </Badge>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-7992">
                        <div className="text-sm text-[#0F172A]" data-id="element-7993">
                          {formatDate(order.date)}
                        </div>
                        <div className="text-xs text-[#94A3B8] mt-0.5" data-id="element-7994">
                          {formatTime(order.date)}
                        </div>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-7995">
                        <span className="text-sm text-[#475569]" data-id="element-7996">
                          {order.items} items
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-7997">
                        <span className="text-sm font-semibold text-[#0F172A]" data-id="element-7998">
                          ${order.total.toFixed(2)}
                        </span>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-7999">
                        {order.paymentStatus === 'In Retry' && order.retryCount && order.maxRetries ? <Badge variant="warning" data-id="element-8000">
                            In Retry ({order.retryCount}/{order.maxRetries})
                          </Badge> : <Badge variant={getPaymentStatusVariant(order.paymentStatus)} data-id="element-8001">
                            {order.paymentStatus}
                          </Badge>}
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8002">
                        <Badge variant={getFulfillmentStatusVariant(order.fulfillmentStatus)} data-id="element-8003">
                          {order.fulfillmentStatus}
                        </Badge>
                      </td>
                      <td className="px-3 md:px-4 py-3 md:py-4" data-id="element-8004">
                        <div className="relative" data-id="element-8005">
                          <button onClick={() => setOpenOrderDropdown(openOrderDropdown === order.id ? null : order.id)} className="p-2 hover:bg-[#F1F5F9] rounded-md transition-colors duration-200 ease-in-out" data-id="element-8006">
                            <MoreVertical className="w-5 h-5 text-[#64748B]" data-id="element-8007" />
                          </button>
                          {openOrderDropdown === order.id && <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenOrderDropdown(null)} data-id="element-8008" />
                              <div className="absolute right-0 mt-2 w-56 bg-white border border-[#CBD5E1] rounded-md shadow-lg z-20 py-1" data-id="element-8009">
                                <button onClick={() => handleViewOrderInShopify(order.id)} className="w-full px-4 py-2 text-left text-sm hover:bg-[#F8FAFC] transition-colors text-[#0F172A] flex items-center gap-2" data-id="element-8010">
                                  <ExternalLink className="w-4 h-4" data-id="element-8011" />
                                  View Order in Shopify
                                </button>
                              </div>
                            </>}
                        </div>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4" data-id="element-8012">
            <div className="text-[13px] text-[#64748B]" data-id="element-8013">
              Showing {(currentPage_ - 1) * pageSize + 1}-
              {Math.min(currentPage_ * pageSize, filteredOrders.length)} of{' '}
              {filteredOrders.length} orders
            </div>
            <div className="flex items-center gap-2" data-id="element-8014">
              <button onClick={() => setCurrentPage_(Math.max(1, currentPage_ - 1))} disabled={currentPage_ === 1} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-8015">
                Previous
              </button>
              <div className="flex items-center gap-1" data-id="element-8016">
                {Array.from({
              length: totalPages
            }, (_, i) => i + 1).map(page => <button key={page} onClick={() => setCurrentPage_(page)} className={`w-10 h-10 rounded-md text-[13px] font-medium transition-colors duration-200 ease-in-out ${page === currentPage_ ? 'bg-[#0EA5E9] text-white' : 'text-[#475569] hover:bg-[#F8FAFC]'}`} data-id="element-8017">
                    {page}
                  </button>)}
              </div>
              <button onClick={() => setCurrentPage_(Math.min(totalPages, currentPage_ + 1))} disabled={currentPage_ === totalPages} className="px-4 py-2 border border-[#CBD5E1] rounded-md text-[13px] font-medium text-[#475569] hover:bg-[#F8FAFC] disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 ease-in-out" data-id="element-8018">
                Next
              </button>
            </div>
          </div>
        </> : <>
          <div className="bg-white rounded-lg border border-[#E2E8F0] p-8 md:p-12 text-center shadow-[0_1px_3px_rgba(15,23,42,0.08)]" data-id="element-8019">
            <Package className="w-12 h-12 md:w-16 md:h-16 text-[#CBD5E1] mx-auto mb-4" data-id="element-8020" />
            <h3 className="text-lg md:text-xl font-semibold text-[#0F172A] mb-2" data-id="element-8021">
              No orders found
            </h3>
            <p className="text-sm md:text-base text-[#64748B] mb-6" data-id="element-8022">
              Try adjusting your filters or date range
            </p>
            {hasActiveFilters && <Button variant="outline" onClick={handleClearAll} data-id="element-8023">
                Clear all filters
              </Button>}
          </div>
        </>}
    </div>;
}